import React, { useState, useEffect, useRef, useCallback } from "react";
import { io } from "socket.io-client";
import { useNavigate } from "react-router-dom";
import "./DriverDashboard.css";
import { LogoutButton } from "../Common/LogoutButton";
import {
  acceptRide,
  completeRide,
  submitMaintenanceRequest,
} from "../../Services/api";
import { apiFetch } from "../../utils/api"; // adjust path
import { toast } from "react-toastify";
import { DriverRideTimer } from "./DriverRideTimer.js";
import { subscribeUserToPush } from "../../utils/notification.js";

const SOCKET_URL = process.env.REACT_APP_BASE_URL;

const DriverDashboard = ({ driver }) => {
  // All state declarations at the top
  const [ongoingRide, setOngoingRide] = useState(null);
  const [newRideRequests, setNewRideRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [rideEndedMessage, setRideEndedMessage] = useState("");
  const [rideAcceptedMessage, setRideAcceptedMessage] = useState("");
  const [socket, setSocket] = useState(null);
  const [activeTab, setActiveTab] = useState("active");
  const [rideHistory, setRideHistory] = useState([]);
  const [materialPicked, setMaterialPicked] = useState(false);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [selectedMaintenanceType, setSelectedMaintenanceType] = useState("");
  const [maintenanceReason, setMaintenanceReason] = useState("");
  const [maintenanceSubmitted, setMaintenanceSubmitted] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState("");
  const [rideStarted, setRideStarted] = useState(false);
  const [materialDropped, setMaterialDropped] = useState(false);
  const [rideId, setRideId] = useState(null);
  const [top3ClosestRides, setTop3ClosestRides] = useState([]);
  const [otherAvailableRides, setOtherAvailableRides] = useState([]);
  const [isEndingRide, setIsEndingRide] = useState(false);
  const [operatorId, setOperatorId] = useState(null);
  const [operatorName, setOperatorName] = useState(null);
  const [operatorVehicleType, setOperatorVehicleType] = useState(null);
  const [requesterDetails, setRequesterDetails] = useState(null);
  const [customRemark, setCustomRemark] = useState("");
  const [breakdownType, setBreakdownType] = useState("");
  const [isBlocked, setIsBlocked] = useState(false);
  const [maintenanceCompleteNotice, setMaintenanceCompleteNotice] =
    useState(false);
  const [wasInPartialBreakdown, setWasInPartialBreakdown] = useState(false);
  const [isManuallyBlocked, setIsManuallyBlocked] = useState(false);
  const [rideTimestamps, setRideTimestamps] = useState({});
  const [showRequesterPopup, setShowRequesterPopup] = useState(false);
  const [refreshDriverName, setRefreshDriverName] = useState(0);
  const [operatorVehicle, setOperatorVehicle] = useState(null);
  // const [splTaskStTime, setSplTaskStTime] = useState(null);
  // const [splTaskDuration, setSplTaskDuration] = useState(null);
  const [splTaskData, setSplTaskData] = useState({});

  const ongoingRideRef = useRef(null);

  // Escape < and > to prevent HTML/script injection
  const sanitizeText = (str) => {
    if (!str) return "";
    return String(str).replace(/</g, "&lt;").replace(/>/g, "&gt;");
  };

  // Function to refresh driver data and check blocking status
  const refreshDriverData = () => {
    setRefreshDriverName((prev) => prev + 1);
  };

  const navigate = useNavigate();

  const maintenanceReasons = {
    forklift: [
      "Engine Issue",
      "Hydraulic Problem",
      "Tire Replacement",
      "Electrical Fault",
      "Others",
    ],
    crane: [
      "Boom Issue",
      "Hydraulic Leak",
      "Cable Problem",
      "Engine Trouble",
      "Others",
    ],
    other: ["General Maintenance", "Safety Check", "Unknown Issue", "Others"],
  };

  // All useEffect hooks at the top in order

  const fetchDriverName = async (storedUser) => {
    try {
      const data = await apiFetch(
        `/api/drivers/getSpecificDriver/${storedUser.token_no}`
      );

      // console.log("driver data bosir = ", data);

      if (data.driver && data.driver.length > 0) {
        setOperatorName(data.driver[0].NAME);
        setOperatorVehicleType(data.driver[0].VEHICLE_TYPE);

        const maintenanceBlocked = data.driver[0].BLOCKED_FOR_MAINTENANCE === 1;
        setIsBlocked(maintenanceBlocked);

        const vehicle_type = data.driver[0].VEHICLE_TYPE;
        // console.log("vehicle_type = ", vehicle_type);
        setOperatorVehicle(vehicle_type);

        console.log(`Driver maintenance block status: ${maintenanceBlocked}`);
      } else {
        setOperatorName(storedUser.name); // fallback
      }
    } catch (err) {
      console.error("Failed to fetch driver name:", err);
      setOperatorName(storedUser.name); // fallback
    }
  };

  // for notifications
  useEffect(() => {
    console.log("🚀 useEffect fired — subscribing user to push...");
    subscribeUserToPush();
  }, []);

  useEffect(() => {
    const storedUserString = localStorage.getItem("user");

    if (!storedUserString) {
      navigate("/signin", { replace: true });
      return;
    }

    try {
      const storedUser = JSON.parse(storedUserString);
      // console.log("storedUser = ", storedUser);

      fetchRideHistory(storedUser.token_no);
      fetchNewRideRequests(storedUser.token_no);
      setOperatorId(storedUser.token_no);

      fetchDriverName(storedUser);
    } catch (err) {
      console.error("Failed to parse stored user:", err);
      navigate("/signin", { replace: true });
    }
  }, [navigate, refreshDriverName]); // ✅ driver name refresh

  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === "user") {
        setRefreshDriverName((prev) => prev + 1); // trigger re-read
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  useEffect(() => {
    if (!driver) {
      navigate("/signin");
    }
  }, [driver]);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (!storedUser?.token_no) return; // wait until available

    const savedRide = localStorage.getItem(
      `ongoingRide_${storedUser.token_no}`
    );

    if (!savedRide) {
      console.log("⚠️ No ongoing ride found in localStorage");
      return;
    }

    const rideIdData = JSON.parse(savedRide);
    const splTaskRideId = rideIdData?.RIDE_ID;
    if (!splTaskRideId) return;

    const savedSplData = localStorage.getItem(`splTaskData_${splTaskRideId}`);
    if (savedSplData) {
      const parsed = JSON.parse(savedSplData);
      setSplTaskData({
        SPL_TASK_ST_TIME: parsed.SPL_TASK_ST_TIME,
        SPL_RIDE_DURATION: parsed.SPL_RIDE_DURATION,
        RIDE_ID: splTaskRideId,
      });
    }
  }, []); // ✅ run only once

  // useEffect(() => {
  //   if (rideId) {
  //     const savedSplData = localStorage.getItem(`splTaskData_${rideId}`);
  //     console.log("lsdfjdkflf = ", savedSplData);
  //     if (savedSplData) {
  //       setSplTaskData(JSON.parse(savedSplData));
  //     }
  //   }
  // }, []);

  // In DriverDashboard.js, in the useEffect with socket setup, remove the duplicate statusChanged listener
  useEffect(() => {
    const newSocket = io(SOCKET_URL, {
      autoConnect: true,
      withCredentials: true,
    });
    setSocket(newSocket);

    newSocket.on("connect", () => {
      console.log("Connected to DriverDashboard Socket.IO server");
    });

    newSocket.on("disconnect", () => {
      console.log("Disconnected from DriverDashboard Socket.IO server");
    });

    newSocket.onAny((event, ...args) => {
      console.log(`Received event: ${event}`, args);
    });

    newSocket.on("removeRide", ({ rideId }) => {
      setNewRideRequests((prevRides) =>
        prevRides.filter((ride) => ride.RIDE_ID !== rideId)
      );
    });

    newSocket.on("driverBlocked", ({ driverId }) => {
      const currentUser = JSON.parse(localStorage.getItem("user"));
      if (currentUser?.token_no === driverId) {
        console.log("Driver blocked for maintenance");
        setIsBlocked(true);
      }
    });

    newSocket.on("maintenanceCompleted", (data) => {
      const currentUser = JSON.parse(localStorage.getItem("user"));
      if (currentUser?.token_no === data.driverId) {
        console.log("✅ Maintenance completed notification:", data);
        setIsBlocked(false);
        setMaintenanceCompleteNotice(true);

        // Show success message
        if (data.message) {
          toast.success("✅ " + data.message);
        }

        // Refresh driver data
        refreshDriverData();
      }
    });

    // Handle maintenance rejection notifications
    newSocket.on("maintenanceRejected", (data) => {
      const currentUser = JSON.parse(localStorage.getItem("user"));
      if (currentUser?.token_no === data.driverId) {
        console.log("❌ Maintenance rejected notification:", data);
        setIsBlocked(false);

        // Show rejection message with reason
        if (data.message) {
          toast.error("❌ " + data.message);
        }

        // Refresh driver data
        refreshDriverData();
      }
    });

    // 🚨 REAL-TIME: Handle driver unblocking
    newSocket.on("driverUnblocked", (data) => {
      const currentUser = JSON.parse(localStorage.getItem("user"));
      if (currentUser?.token_no === data.driverId) {
        console.log("🔓 Driver unblocked in real-time:", data);
        setIsBlocked(false); // Unblock immediately

        // Show appropriate message based on reason
        if (data.message) {
          if (data.reason === "maintenance_completed") {
            toast.success("✅ " + data.message);
          } else if (data.reason === "maintenance_rejected") {
            toast.error("❌ " + data.message);
          }
        }

        // Refresh driver data
        refreshDriverData();
      }
    });

    newSocket.on("statusChanged", ({ driverId, status }) => {
      const currentUser = JSON.parse(localStorage.getItem("user"));
      console.log("currentUser = ", currentUser);
      if (currentUser?.token_no === driverId) {
        if (status === "Not Active") {
          console.log("Driver manually blocked by admin");
          setIsManuallyBlocked(true);
          localStorage.setItem("manualBlock", "true");
        } else if (status === "Active") {
          console.log("Driver manually unblocked by admin");
          setIsManuallyBlocked(false);
          localStorage.removeItem("manualBlock");
        }
      }
    });

    newSocket.on("newRideRequest", (rideData) => {
      const currentUser = JSON.parse(localStorage.getItem("user"));
      const jwt_token = currentUser.jwt_token;

      if (rideData.assignedTo === currentUser.token_no) {
        const normalizedRide = {
          RIDE_ID: rideData.rideId,
          FROM_LOCATION: rideData.fromLocation,
          TO_LOCATION: rideData.toLocation,
          IS_SPECIAL_TASK: rideData.isSpecialTask,
          REQUESTER_DETAILS: rideData.requesterData,
          VEHICLE_TYPE: rideData.vehicleType,
        };

        console.log("special ride data = ", rideData);

        setNewRideRequests((prevRides) => {
          const exists = prevRides.some(
            (r) => r.RIDE_ID === normalizedRide.RIDE_ID
          );
          return exists ? prevRides : [normalizedRide, ...prevRides];
        });
      }

      fetchNewRideRequests(currentUser.token_no, jwt_token);
    });

    newSocket.on("redistributedRide", (rideData) => {
      const currentUser = JSON.parse(localStorage.getItem("user"));

      if (
        rideData.assignedVehicleType === currentUser.assignedVehicleType &&
        rideData.originalDriverId !== currentUser.token_no
      ) {
        const redistributedRide = {
          RIDE_ID: rideData.rideId,
          FROM_LOCATION: rideData.fromLocation,
          TO_LOCATION: rideData.toLocation,
          IS_SPECIAL_TASK: rideData.isSpecialTask,
          VEHICLE_TYPE: rideData.vehicleType,
          MAINTENANCE_COMMENT: rideData.maintenanceComment,
          IS_REDISTRIBUTED: true,
          ORIGINAL_DRIVER_NAME: rideData.originalDriverName,
          REQUESTER_DETAILS: rideData.requesterDetails,
        };

        setNewRideRequests((prevRides) => {
          const exists = prevRides.some(
            (r) => r.RIDE_ID === redistributedRide.RIDE_ID
          );
          return exists ? prevRides : [redistributedRide, ...prevRides];
        });
      }
    });

    newSocket.on("rideStarted", (data) => {
      // console.log("rideStarted received for ride:", data.rideId);
      // console.log("Current ongoingRide ref:", ongoingRideRef.current);

      if (data.rideId === ongoingRideRef.current?.RIDE_ID) {
        console.log("rideStarted matched, updating state");
        setRideStarted(true);
        fetchRideTimestamps(data.rideId);
      } else {
        console.log("rideStarted not matched:", {
          received: data.rideId,
          current: ongoingRideRef.current?.RIDE_ID,
        });
      }
    });

    newSocket.on("materialPicked", (data) => {
      if (data.rideId === ongoingRideRef.current?.RIDE_ID) {
        console.log("materialPicked event received, updating state");
        setMaterialPicked(true);
        setMaterialDropped(false);
        fetchRideTimestamps(data.rideId);
      }
    });

    newSocket.on("materialDropped", (data) => {
      if (data.rideId === ongoingRideRef.current?.RIDE_ID) {
        console.log("materialDropped event received, updating state");
        setMaterialDropped(true);
        fetchRideTimestamps(data.rideId);
      }
    });

    newSocket.on("rideEnded", (data) => {
      if (data.rideId === ongoingRideRef.current?.RIDE_ID) {
        console.log("rideEnded event received, clearing ongoing ride");
        setOngoingRide(null);
        setRideStarted(false);
        setMaterialPicked(false);
        setMaterialDropped(false);
        localStorage.removeItem(`ongoingRide_${operatorId}`);
        localStorage.removeItem(`rideStates_${operatorId}`);
      }
    });

    return () => {
      newSocket.disconnect();
    };
  }, []);

  useEffect(() => {
    if (ongoingRide) {
      const interval = setInterval(() => {
        setProgress((prev) => (prev >= 100 ? 100 : prev + 10));
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [ongoingRide]);

  // useEffect(() => {
  //   console.log("New rides updated:", newRideRequests);
  // }, [newRideRequests]);

  useEffect(() => {
    setTop3ClosestRides(newRideRequests.slice(0, 3));
    setOtherAvailableRides(newRideRequests.slice(3));
  }, [newRideRequests]);

  useEffect(() => {
    if (rideAcceptedMessage) {
      toast.success(rideAcceptedMessage);
    }
  }, [rideAcceptedMessage]);

  const fetchRideTimestamps = useCallback(async (rideId) => {
    try {
      const data = await apiFetch(`/api/bookings/get-timestamps/${rideId}`);
      setRideTimestamps(data.timestamps);
    } catch (error) {
      console.error("Error fetching ride timestamps:", error);
      setRideTimestamps({}); // Clear timestamps on error
    }
  }, []);

  // Restore ongoing ride and states from localStorage
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (!storedUser) return;

    const savedRide = localStorage.getItem(
      `ongoingRide_${storedUser.token_no}`
    );

    // console.log("savedRide = ", savedRide);
    const savedStates = localStorage.getItem(
      `rideStates_${storedUser.token_no}`
    );

    if (savedRide) {
      const parsedRide = JSON.parse(savedRide); // ✅ parse saved ride
      setOngoingRide(parsedRide);

      if (parsedRide.requesterDetails) {
        setRequesterDetails(parsedRide.requesterDetails); // ✅ restore requester
      }
    }

    if (savedStates) {
      const states = JSON.parse(savedStates);
      setRideStarted(states.rideStarted);
      setMaterialPicked(states.materialPicked);
      setMaterialDropped(states.materialDropped);
    }
  }, []);

  // Persist ride state changes to localStorage
  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    if (!storedUser) return;
    if (ongoingRide && operatorId) {
      localStorage.setItem(
        `rideStates_${storedUser.token_no}`,
        JSON.stringify({
          rideStarted,
          materialPicked,
          materialDropped,
        })
      );
      localStorage.setItem(
        `ongoingRide_${storedUser.token_no}`,
        JSON.stringify({
          ...ongoingRide,
          requesterDetails, // ✅ save requester info too
        })
      );
    }
  }, [
    rideStarted,
    materialPicked,
    materialDropped,
    ongoingRide,
    requesterDetails,
  ]);

  useEffect(() => {
    const checkManualBlockStatus = async () => {
      const user = JSON.parse(localStorage.getItem("user"));
      if (!user) return;

      try {
        const data = await apiFetch(`/api/drivers/status/${user.token_no}`);

        if (data.isActive === 1) {
          setIsManuallyBlocked(false);
          localStorage.removeItem("manualBlock");
        } else {
          setIsManuallyBlocked(true);
          localStorage.setItem("manualBlock", "true");
        }
      } catch (err) {
        console.error("Error validating manual block status:", err);
      }
    };

    checkManualBlockStatus();
  }, []);

  useEffect(() => {
    const manualBlock = localStorage.getItem("manualBlock");
    if (manualBlock === "true") {
      setIsManuallyBlocked(true);
    }
  }, []);

  // Keep ongoingRideRef always in sync with latest ongoingRide state
  useEffect(() => {
    ongoingRideRef.current = ongoingRide;
  }, [ongoingRide]);

  // Handler functions
  const handleMaintenanceSubmit = async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (!user) return;

    if (!selectedMaintenanceType) {
      setMaintenanceMessage("Please select a vehicle type.");
      return;
    }

    // Mid-ride: comment is mandatory
    if (ongoingRide && !customRemark.trim()) {
      setMaintenanceMessage("Please enter a comment for maintenance.");
      return;
    }

    // Idle: maintenance reason & breakdown type are mandatory
    if (!ongoingRide && (!maintenanceReason || !breakdownType)) {
      setMaintenanceMessage(
        "Please select maintenance reason and breakdown type."
      );
      return;
    }

    const finalReason =
      maintenanceReason === "Others" ? customRemark.trim() : maintenanceReason;

    try {
      const maintenanceData = {
        driver_id: operatorId,
        driver_name: operatorName,
        vehicle_type: selectedMaintenanceType,
        issue: ongoingRide ? "Mid-ride maintenance" : finalReason,
        comment_text: customRemark.trim(),
        status: "pending",
        breakdown_type: ongoingRide ? "partial" : breakdownType,
        ride_id: ongoingRide?.RIDE_ID || null,
      };

      // Submit maintenance request
      await submitMaintenanceRequest(maintenanceData);

      // If mid-ride, also redistribute the ride
      if (ongoingRide) {
        await apiFetch(`/api/maintenance/redistribute-ride`, {
          method: "POST",
          body: JSON.stringify({
            rideId: ongoingRide.RIDE_ID,
            originalDriverId: operatorId,
            vehicleType: selectedMaintenanceType,
            maintenanceComment: customRemark.trim(),
            rideData: {
              ...ongoingRide,
              MAINTENANCE_COMMENT: customRemark.trim(),
              ORIGINAL_DRIVER_ID: operatorId,
              ORIGINAL_DRIVER_NAME: operatorName,
              STATUS: "redistributed_due_to_maintenance",
            },
          }),
        });

        // Clear current ride from this driver
        setOngoingRide(null);
        setRideStarted(false);
        setMaterialPicked(false);
        setMaterialDropped(false);

        // Clear localStorage
        localStorage.removeItem(`ongoingRide_${operatorId}`);
        localStorage.removeItem(`rideStates_${operatorId}`);
      }

      // Block the driver immediately after maintenance submission
      setIsBlocked(true);

      setMaintenanceMessage(
        "Maintenance request submitted successfully! You are now blocked until maintenance is completed."
      );
      setMaintenanceSubmitted(true);

      // Close modal immediately after successful submission
      setTimeout(() => {
        setShowMaintenanceModal(false);
        setSelectedMaintenanceType("");
        setMaintenanceReason("");
        setBreakdownType("");
        setCustomRemark("");
        setMaintenanceMessage("");
        setMaintenanceSubmitted(false);
      }, 1500);
    } catch (error) {
      console.error("Error submitting maintenance request:", error);
      setMaintenanceMessage("Failed to submit maintenance request");
    }
  };

  const fetchNewRideRequests = async (opid, jwt_token) => {
    try {
      // Fetch driver details
      const data = await apiFetch(`/api/drivers/getSpecificDriver/${opid}`);

      // console.log("driver dsjdsk data = ", data);
      const driver = data.driver[0];
      const assignedVehicle = driver?.VEHICLE_TYPE;

      // Fetch rides by assigned vehicle type
      const rideData = await apiFetch(
        `/api/drivers/available-rides?vehicleType=${assignedVehicle}`
      );

      console.log("rideData resp - ", rideData);

      // Filter by assigned vehicle type (just to be safe)
      const matchingRides = (rideData.rides.rows || []).filter(
        (ride) => ride.VEHICLE_TYPE === assignedVehicle
      );

      // Add normalized special task flag
      const normalizedRides = matchingRides.map((ride) => ({
        ...ride,
        IS_SPECIAL_TASK: ride.RIDE_TYPE === "Special Task",
      }));

      // Remove duplicates by RIDE_ID
      const uniqueRides = Array.from(
        new Map(normalizedRides.map((ride) => [ride.RIDE_ID, ride])).values()
      );

      setNewRideRequests(uniqueRides || []);
    } catch (error) {
      console.error("❌ Error fetching filtered rides:", error);
    }
  };

  const handleAcceptRide = async (rideId) => {
    if (isBlocked || isManuallyBlocked) {
      toast.info("You are currently blocked and cannot accept rides.");
      return;
    }

    if (loading) {
      console.log("loading and returning.....");
      return;
    }

    const acceptedRide = newRideRequests.find(
      (ride) => ride.RIDE_ID === rideId
    );
    if (!acceptedRide) {
      console.error("Ride not found");
      return;
    }

    setLoading(true);

    console.log("acceptedRide = ", acceptedRide);
    try {
      const isRedistributed =
        acceptedRide.IS_REDISTRIBUTED ||
        acceptedRide.STATUS === "redistributed_due_to_maintenance";

      if (isRedistributed) {
        // ✅ Redistributed ride
        const result = await apiFetch(
          `/api/maintenance/accept-redistributed-ride`,
          {
            method: "POST",
            body: JSON.stringify({
              rideId,
              newDriverId: operatorId,
              currentState: acceptedRide.RIDE_STATE || "accepted",
            }),
          }
        );

        console.log("✅ Redistributed ride accepted:", result);

        const currentState = acceptedRide.RIDE_STATE || "accepted";
        switch (currentState) {
          case "started":
            setRideStarted(true);
            setMaterialPicked(false);
            setMaterialDropped(false);
            break;
          case "material_picked":
            setRideStarted(true);
            setMaterialPicked(true);
            setMaterialDropped(false);
            break;
          case "material_dropped":
            setRideStarted(true);
            setMaterialPicked(true);
            setMaterialDropped(true);
            break;
          default:
            setRideStarted(false);
            setMaterialPicked(false);
            setMaterialDropped(false);
        }
      } else {
        // ✅ Regular ride
        await apiFetch(`/api/ride-history/update-status/user/${rideId}`, {
          method: "PUT",
          body: JSON.stringify({ status: "accepted" }),
        });

        await acceptRide(rideId, operatorId);

        // ⏳ Fetch the updated ride with SPL_TASK_START_TIME
        const updatedRideResp = await apiFetch(`/api/tracking/rides/${rideId}`);

        console.log("splData = ", updatedRideResp.ride);
        setSplTaskData({
          SPL_TASK_ST_TIME: updatedRideResp.ride.SPL_TASK_START_TIME,
          SPL_RIDE_DURATION: updatedRideResp.ride.SPECIAL_RIDE_DURATION,
          RIDE_ID: rideId,
        });

        console.log("splTaskData = ", splTaskData);

        localStorage.setItem(
          `splTaskData_${rideId}`,
          JSON.stringify({
            SPL_TASK_ST_TIME: updatedRideResp.ride.SPL_TASK_START_TIME,
            SPL_RIDE_DURATION: updatedRideResp.ride.SPECIAL_RIDE_DURATION,
            rideId,
          })
        );

        setRideStarted(false);
        setMaterialPicked(false);
        setMaterialDropped(false);
      }

      // ✅ Driver data
      const driverDataResp = await apiFetch(
        `/api/drivers/getSpecificDriver/${operatorId}`
      );
      const driverData = driverDataResp.driver[0];

      // ✅ Requester data
      const user_id = acceptedRide.USER_ID;
      const requesterDataResp = await apiFetch(
        `/api/drivers/getSpecificRequester/${user_id}`
      );
      const requesterData = requesterDataResp.requester;

      setRequesterDetails({
        name: requesterData.FULL_NAME,
        mobile: requesterData.MOBILE,
      });

      localStorage.setItem(
        `requesterDetails_${rideId}`,
        JSON.stringify({
          name: requesterData.FULL_NAME,
          mobile: requesterData.MOBILE,
        })
      );

      setRideId(acceptedRide.RIDE_ID);
      setOngoingRide(acceptedRide);
      ongoingRideRef.current = acceptedRide;
      setNewRideRequests((prev) =>
        prev.filter((ride) => ride.RIDE_ID !== rideId)
      );

      setRideAcceptedMessage(
        isRedistributed
          ? "Redistributed ride accepted successfully! Continuing from previous state."
          : "Ride accepted successfully!"
      );

      if (rideId) {
        localStorage.setItem(
          `driverDetails-${rideId}`,
          JSON.stringify(driverData)
        );
      }

      if (operatorId) {
        localStorage.setItem(
          `ongoingRide_${operatorId}`,
          JSON.stringify(acceptedRide)
        );
        localStorage.setItem(
          `rideStates_${operatorId}`,
          JSON.stringify({
            rideStarted,
            materialPicked,
            materialDropped,
          })
        );
      }

      if (socket && !isRedistributed) {
        socket.emit("rideAccepted", { rideId, driverData });
      }

      fetchRideTimestamps(rideId);
    } catch (error) {
      setRideAcceptedMessage("Failed to accept ride.");
      console.error("Error accepting ride:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStartRide = async () => {
    if (isManuallyBlocked || !ongoingRide || !socket) return;

    console.log("Starting ride for ID:", ongoingRide.RIDE_ID);

    try {
      // Update user ride history if not a special task
      if (!ongoingRide.IS_SPECIAL_TASK) {
        const userHistoryData = await apiFetch(
          `/api/ride-history/update-status/user/${ongoingRide.RIDE_ID}`,
          {
            method: "PUT",
            body: JSON.stringify({ status: "started" }),
          }
        );
        // console.log("User ride history updated:", userHistoryData);
      }

      // Update ride start timestamp
      await apiFetch("/api/bookings/update-timestamps", {
        method: "PUT",
        body: JSON.stringify({ rideId: ongoingRide.RIDE_ID, event: "start" }),
      });
      console.log("Start timestamp updated");

      setRideStarted(true);
      await fetchRideTimestamps(ongoingRide.RIDE_ID);

      // Emit socket event
      if (socket.connected) {
        socket.emit("rideStarted", { rideId: ongoingRide.RIDE_ID });
        console.log("rideStarted event emitted for:", ongoingRide.RIDE_ID);
      } else {
        console.warn("Socket not connected. Could not emit rideStarted.");
      }
    } catch (err) {
      console.error("Error during handleStartRide:", err);
    }
  };

  const handleMaterialPicked = async () => {
    if (isManuallyBlocked || !ongoingRide || !socket) return;

    try {
      // Update user ride history if not a special task
      if (!ongoingRide.IS_SPECIAL_TASK) {
        const materialData = await apiFetch(
          `/api/ride-history/update-status/user/${ongoingRide.RIDE_ID}`,
          {
            method: "PUT",
            body: JSON.stringify({ status: "material picked" }),
          }
        );
        console.log("Material data updated:", materialData);
      }

      // Update ride timestamp
      await apiFetch("/api/bookings/update-timestamps", {
        method: "PUT",
        body: JSON.stringify({
          rideId: ongoingRide.RIDE_ID,
          event: "materialPicked",
        }),
      });
      console.log("Material picked timestamp updated");

      // Update local state
      setMaterialPicked(true);
      setMaterialDropped(false);

      // Emit socket event
      if (socket.connected) {
        socket.emit("materialPicked", { rideId: ongoingRide.RIDE_ID });
        console.log("materialPicked event emitted for:", ongoingRide.RIDE_ID);
      } else {
        console.warn("Socket not connected. Could not emit materialPicked.");
      }

      // Fetch latest ride timestamps
      fetchRideTimestamps(ongoingRide.RIDE_ID);
    } catch (error) {
      console.error("Error during handleMaterialPicked:", error);
    }
  };

  const handleMaterialDropped = async () => {
    if (isManuallyBlocked || !ongoingRide || !socket) return;

    try {
      // Update user ride history if not a special task
      if (!ongoingRide.IS_SPECIAL_TASK) {
        const droppedData = await apiFetch(
          `/api/ride-history/update-status/user/${ongoingRide.RIDE_ID}`,
          {
            method: "PUT",
            body: JSON.stringify({ status: "delivered" }),
          }
        );
        console.log("Dropped material data:", droppedData);
      }

      // Update ride timestamp
      await apiFetch("/api/bookings/update-timestamps", {
        method: "PUT",
        body: JSON.stringify({
          rideId: ongoingRide.RIDE_ID,
          event: "materialDropped",
        }),
      });
      console.log("Material dropped timestamp updated");

      // Update local state
      setMaterialDropped(true);

      // Emit socket event
      if (socket.connected) {
        socket.emit("materialDropped", { rideId: ongoingRide.RIDE_ID });
        console.log("materialDropped event emitted for:", ongoingRide.RIDE_ID);
      } else {
        console.warn("Socket not connected. Could not emit materialDropped.");
      }

      // Fetch latest ride timestamps
      fetchRideTimestamps(ongoingRide.RIDE_ID);
    } catch (error) {
      console.error("Error during handleMaterialDropped:", error);
    }
  };

  const handleEndRide = async () => {
    if (isManuallyBlocked || !ongoingRide || !socket) return;

    setIsEndingRide(true);
    console.log("operator Id =", ongoingRide);

    try {
      const completedRide = {
        RIDE_ID: ongoingRide.RIDE_ID,
        FROM_LOCATION: ongoingRide.FROM_LOCATION,
        TO_LOCATION: ongoingRide.TO_LOCATION,
        VEHICLE_TYPE: ongoingRide.VEHICLE_TYPE,
        RIDE_TYPE: ongoingRide.IS_SPECIAL_TASK ? "Special Task" : "Regular",
        COMPLETED_AT: new Date(),
        DRIVER_ID: operatorId,
      };

      // Complete ride in backend
      await completeRide(ongoingRide.RIDE_ID);

      // Update ride end timestamp
      await apiFetch(`/api/bookings/update-timestamps`, {
        method: "PUT",
        body: JSON.stringify({ rideId: ongoingRide.RIDE_ID, event: "end" }),
      });

      // Update ride history status
      await apiFetch(
        `/api/ride-history/update-status/user/${ongoingRide.RIDE_ID}`,
        {
          method: "PUT",
          body: JSON.stringify({ status: "completed" }),
        }
      );

      // Add completed ride to ride history
      await apiFetch(`/api/ride-history`, {
        method: "POST",
        body: JSON.stringify(completedRide),
      });

      // Update local ride history state
      setRideHistory((prev) => [completedRide, ...prev]);

      // ✅ Show success toast
      toast.success("Ride ended successfully!");

      // Emit rideEnded socket event
      if (socket.connected) {
        socket.emit("rideEnded", { rideId: ongoingRide.RIDE_ID });
        console.log("rideEnded event emitted for:", ongoingRide.RIDE_ID);
      } else {
        console.warn("Socket not connected. Could not emit rideEnded.");
      }

      // Clear persistent local state
      localStorage.removeItem(`ongoingRide_${operatorId}`);
      localStorage.removeItem(`rideStates_${operatorId}`);

      // Reset UI after delay
      setTimeout(() => {
        setOngoingRide(null);
        setProgress(0);
        setMaterialPicked(false);
        setRideAcceptedMessage(null);
        setIsEndingRide(false);
      }, 2000);

      fetchRideTimestamps(ongoingRide.RIDE_ID);
    } catch (error) {
      console.error("Error ending ride:", error);

      // ❌ Show error toast
      toast.error("Failed to end ride. Please try again.");

      setIsEndingRide(false);
    }
  };

  const fetchRideHistory = async (opId) => {
    console.log("Fetching ride history for operator ID =", opId);

    try {
      const data = await apiFetch(`/api/ride-history?driverId=${opId}`);
      // console.log("Ride history data =", data);

      setRideHistory(data.history || []);
    } catch (error) {
      console.error("Error fetching ride history:", error);
    }
  };

  const handleMaintenanceTypeChange = (e) => {
    setSelectedMaintenanceType(e.target.value);
    setMaintenanceReason("");
  };

  // Enhanced formatDate function to handle Oracle dates
  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";

    try {
      let date;

      if (typeof timestamp === "string") {
        // Clean extra characters
        const cleanTimestamp = timestamp.trim().replace(/^["']|["']$/g, "");

        // Try strict ISO parse
        date = new Date(cleanTimestamp);

        // Fallback for Oracle format like "14-JUL-2025 10:34:22"
        if (isNaN(date.getTime())) {
          const oracleMatch = cleanTimestamp.match(
            /(\d{2})-(\w{3})-(\d{4})\s+(\d{2}):(\d{2}):(\d{2})/
          );
          if (oracleMatch) {
            const [, day, month, year, hour, minute, second] = oracleMatch;
            const monthMap = {
              JAN: 0,
              FEB: 1,
              MAR: 2,
              APR: 3,
              MAY: 4,
              JUN: 5,
              JUL: 6,
              AUG: 7,
              SEP: 8,
              OCT: 9,
              NOV: 10,
              DEC: 11,
            };
            date = new Date(
              year,
              monthMap[month.toUpperCase()],
              day,
              hour,
              minute,
              second
            );
          }
        }
      } else {
        date = new Date(timestamp);
      }

      if (isNaN(date.getTime())) {
        console.warn("Invalid date:", timestamp);
        return "Invalid Date";
      }

      return date.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });
    } catch (error) {
      console.error("Error formatting date:", error, timestamp);
      return "Invalid Date";
    }
  };

  // Early return if no driver
  if (!driver) return null;

  // console.log("top3rides = ", top3ClosestRides);
  return (
    <>
      {maintenanceCompleteNotice && (
        <div className="completion-popup">
          <p>✅ Maintenance completed. Your vehicle is now active.</p>
          <button
            className="completion-ok-btn"
            onClick={() => setMaintenanceCompleteNotice(false)}
          >
            Okay
          </button>
        </div>
      )}

      {/* Maintenance Blocking Popup - First Priority */}
      {isBlocked && (
        <div className="full-screen-overlay">
          <div className="blocking-popup maintenance-block">
            <div className="block-icon">🔧</div>
            <h2>Vehicle Under Maintenance</h2>
            <p>
              Your vehicle is currently undergoing maintenance and is
              temporarily out of service.
            </p>
            <p>
              You will be automatically unblocked once the maintenance is
              completed.
            </p>
            <p className="contact-maintenance">
              For more details, please contact the maintenance team.
            </p>
            <div className="block-footer">
              <small>You will be notified once maintenance is complete</small>
            </div>
          </div>
        </div>
      )}

      {/* Admin Manual Blocking Popup - Second Priority */}
      {!isBlocked && isManuallyBlocked && (
        <div className="full-screen-overlay">
          <div className="blocking-popup admin-block">
            <div className="block-icon">🚫</div>
            <h2>Vehicle Blocked by Admin</h2>
            <p>
              Your vehicle has been marked as <strong>Not Active</strong> by the
              administrator.
            </p>
            <p>
              Please contact the admin team for more details and to resolve this
              issue.
            </p>
            <div className="block-footer">
              <small>Contact: Admin Department</small>
            </div>
          </div>
        </div>
      )}

      {/* Main Dashboard - Only show when not blocked */}
      {!isBlocked && !isManuallyBlocked && (
        <div className="driver-dashboard">
          <div className="dashboard-btns">
            <button
              className="maintenance-request-btn"
              onClick={() => setShowMaintenanceModal(true)}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 14.66V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h5.34"></path>
                <polygon points="18 2 22 6 12 16 8 16 8 12 18 2"></polygon>
              </svg>
              Request Maintenance
            </button>

            <div className="logout-section">
              <LogoutButton />
              <div className="operator-id-tag">ID: {operatorId}</div>
              <div className="operator-id-tag">
                VEHICLE: {operatorVehicleType}
              </div>
            </div>
          </div>

          <header className="header-content">
            <div className="hello-message">
              Hello, <strong>{operatorName}</strong>
            </div>
          </header>
          <div className="tabs">
            <button
              className={`tab-button ${activeTab === "active" ? "active" : ""}`}
              onClick={() => setActiveTab("active")}
            >
              Active Rides
            </button>
            <button
              className={`tab-button ${
                activeTab === "history" ? "active" : ""
              }`}
              onClick={() => setActiveTab("history")}
            >
              Ride History
            </button>
          </div>

          {showMaintenanceModal && (
            <div className="modal-overlay">
              <div className="maintenance-modal">
                <div className="modal-header">
                  <h3>Request Vehicle Maintenance</h3>
                  <button
                    className="close-modal-btn"
                    onClick={() => setShowMaintenanceModal(false)}
                  >
                    &times;
                  </button>
                </div>

                <div className="modal-content">
                  {!maintenanceSubmitted ? (
                    <>
                      {/* Always show vehicle type dropdown */}
                      <div className="form-group">
                        <label htmlFor="maintenance-type">Vehicle Type</label>
                        <select
                          id="maintenance-type"
                          value={selectedMaintenanceType}
                          onChange={(e) =>
                            setSelectedMaintenanceType(e.target.value)
                          }
                          className="form-select"
                        >
                          <option value="">Select vehicle type</option>
                          <option value={operatorVehicle}>
                            {operatorVehicle}
                          </option>
                        </select>
                      </div>

                      {/* IF mid-ride, show ONLY comment field */}
                      {ongoingRide && (
                        <div className="form-group mt-2">
                          <label htmlFor="comment">
                            Maintenance Comment{" "}
                            <span style={{ color: "red" }}>*</span>
                          </label>
                          <textarea
                            id="comment"
                            value={customRemark}
                            onChange={(e) => setCustomRemark(e.target.value)}
                            placeholder="Describe the issue that requires maintenance..."
                            className="form-control"
                            rows={4}
                          />
                        </div>
                      )}

                      {/* IF NOT mid-ride, show issue & breakdown dropdowns */}
                      {!ongoingRide && (
                        <>
                          <div className="form-group mt-2">
                            <label htmlFor="maintenance-reason">
                              Reason for Maintenance
                            </label>
                            <select
                              id="maintenance-reason"
                              value={maintenanceReason}
                              onChange={(e) =>
                                setMaintenanceReason(e.target.value)
                              }
                              className="form-select"
                            >
                              <option value="">Select reason</option>
                              <option value="Engine Issue">Engine Issue</option>
                              <option value="Hydraulic Problem">
                                Hydraulic Problem
                              </option>
                              <option value="Tire Replacement">
                                Tire Replacement
                              </option>
                              <option value="Electrical Fault">
                                Electrical Fault
                              </option>
                              <option value="Others">Others</option>
                            </select>
                          </div>

                          <div className="form-group mt-2">
                            <label htmlFor="breakdown-type">
                              Breakdown Type
                            </label>
                            <select
                              id="breakdown-type"
                              value={breakdownType}
                              onChange={(e) => setBreakdownType(e.target.value)}
                              className="form-select"
                            >
                              <option value="">Select type</option>
                              <option value="partial">Partial</option>
                              <option value="full">Full</option>
                            </select>
                          </div>

                          {maintenanceReason === "Others" && (
                            <div className="form-group mt-2">
                              <label htmlFor="custom-remark">
                                Please specify the issue:
                              </label>
                              <input
                                type="text"
                                id="custom-remark"
                                className="form-select"
                                placeholder="Type your issue here"
                                value={customRemark}
                                onChange={(e) =>
                                  setCustomRemark(e.target.value)
                                }
                              />
                            </div>
                          )}
                        </>
                      )}
                    </>
                  ) : (
                    <div className="submission-success">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="48"
                        height="48"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#10b981"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                      </svg>
                      <p>Your maintenance request has been submitted.</p>
                      {ongoingRide && (
                        <p>
                          This ride has been made available to other drivers
                          with the same vehicle type.
                        </p>
                      )}
                    </div>
                  )}

                  {maintenanceMessage && (
                    <div
                      className={`status-message ${
                        maintenanceMessage.includes("success")
                          ? "success"
                          : "error"
                      }`}
                    >
                      {maintenanceMessage}
                    </div>
                  )}
                </div>

                {!maintenanceSubmitted && (
                  <div className="modal-actions">
                    <button
                      className="cancel-btn"
                      onClick={() => setShowMaintenanceModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-primary mt-3"
                      onClick={handleMaintenanceSubmit}
                      disabled={
                        (!ongoingRide &&
                          (!maintenanceReason || !breakdownType)) ||
                        (ongoingRide && !customRemark.trim())
                      }
                    >
                      Submit
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === "active" ? (
            <>
              {ongoingRide ? (
                <div className="ongoing-ride-card">
                  <div
                    className="ride-header"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    <h3>Ongoing Ride</h3>

                    {ongoingRide && (
                      <button
                        style={{
                          backgroundColor: "#3b82f6",
                          color: "white",
                          border: "none",
                          borderRadius: "6px",
                          padding: "6px 10px",
                          cursor: "pointer",
                          fontSize: "14px",
                        }}
                        onClick={() => setShowRequesterPopup(true)}
                      >
                        🙋 Requester Details
                      </button>
                    )}

                    <span
                      className={`ride-type-badge ${
                        ongoingRide.IS_SPECIAL_TASK ? "special" : "regular"
                      }`}
                    >
                      {ongoingRide.IS_SPECIAL_TASK
                        ? "Special Task Ride"
                        : "Regular Ride"}
                    </span>
                  </div>

                  <div className="ride-details">
                    <p>
                      <strong>Ride ID:</strong> {ongoingRide.RIDE_ID}
                    </p>
                    <p>
                      <strong>From:</strong> {ongoingRide.FROM_LOCATION}
                    </p>
                    <p>
                      <strong>To:</strong> {ongoingRide.TO_LOCATION}
                    </p>
                    <p>
                      <strong>Status:</strong>
                      {!rideStarted
                        ? "Ready to Start"
                        : !materialPicked
                        ? "En Route"
                        : !materialDropped
                        ? "Material Picked"
                        : "Material Dropped"}
                    </p>
                  </div>
                  {ongoingRide.IS_SPECIAL_TASK && (
                    <DriverRideTimer
                      ride={splTaskData}
                      onEnd={handleEndRide} // auto end when timer hits 0
                    />
                  )}

                  <div className="ride-actions">
                    {!rideStarted ? (
                      <button
                        className="start-ride-btn"
                        onClick={handleStartRide}
                      >
                        Start Ride
                      </button>
                    ) : !materialPicked ? (
                      <button
                        className="material-picked-btn"
                        onClick={handleMaterialPicked}
                      >
                        Material Picked
                      </button>
                    ) : !materialDropped ? (
                      <button
                        className="material-dropped-btn"
                        onClick={handleMaterialDropped}
                      >
                        Material Dropped
                      </button>
                    ) : (
                      <button
                        className="end-ride-btn"
                        onClick={handleEndRide}
                        disabled={isEndingRide}
                      >
                        {isEndingRide ? "Ending Ride..." : "End Ride"}
                      </button>
                    )}
                  </div>

                  {rideEndedMessage && (
                    <div className="status-message success">
                      {rideEndedMessage}
                    </div>
                  )}
                </div>
              ) : (
                <div className="no-rides-card">
                  <p>No ongoing ride at the moment</p>
                </div>
              )}

              {/* {rideAcceptedMessage && (
                <div className="status-message success">
                  {rideAcceptedMessage}
                </div>
              )} */}

              {!ongoingRide && (
                <div className="available-rides-section">
                  <h3 className="section-title">Top 3 Latest Rides</h3>
                  {top3ClosestRides.length > 0 ? (
                    <div className="table-container">
                      <table className="rides-table">
                        <thead>
                          <tr>
                            <th>Ride ID</th>
                            <th>From</th>
                            <th>To</th>
                            <th>Vehicle</th>
                            <th>Spl Task Duration</th>
                            <th>Type</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {top3ClosestRides.map((ride) => (
                            <tr
                              key={ride.RIDE_ID}
                              className={
                                ride.IS_REDISTRIBUTED ||
                                ride.STATUS ===
                                  "redistributed_due_to_maintenance"
                                  ? "redistributed-ride"
                                  : ""
                              }
                            >
                              <td>{ride.RIDE_ID}</td>
                              <td>{sanitizeText(ride.FROM_LOCATION)}</td>
                              <td>{sanitizeText(ride.TO_LOCATION)}</td>
                              <td>{ride.VEHICLE_TYPE || "N/A"}</td>
                              <td>{ride.SPECIAL_RIDE_DURATION}</td>
                              <td>
                                <span
                                  className={`ride-type-badge ${
                                    ride.IS_SPECIAL_TASK ? "special" : "regular"
                                  }`}
                                >
                                  {ride.IS_SPECIAL_TASK
                                    ? "Special Task Ride"
                                    : "Regular Ride"}
                                </span>
                                {(ride.IS_REDISTRIBUTED ||
                                  ride.STATUS ===
                                    "redistributed_due_to_maintenance") && (
                                  <div className="redistributed-info">
                                    <span className="redistributed-badge">
                                      🔄 Redistributed
                                    </span>
                                  </div>
                                )}
                              </td>
                              <td>
                                <button
                                  className={`accept-btn ${
                                    ride.IS_REDISTRIBUTED ||
                                    ride.STATUS ===
                                      "redistributed_due_to_maintenance"
                                      ? "priority-btn"
                                      : ""
                                  }`}
                                  onClick={() => handleAcceptRide(ride.RIDE_ID)}
                                  disabled={loading}
                                >
                                  {loading
                                    ? "Processing..."
                                    : ride.IS_REDISTRIBUTED ||
                                      ride.STATUS ===
                                        "redistributed_due_to_maintenance"
                                    ? "Take Over Ride"
                                    : "Accept Ride"}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="no-rides-message">
                      No closest rides available
                    </div>
                  )}

                  <h3 className="section-title">Other Available Rides</h3>
                  {otherAvailableRides.length > 0 ? (
                    <div className="table-container">
                      <table className="rides-table">
                        <thead>
                          <tr>
                            <th>Ride ID</th>
                            <th>From</th>
                            <th>To</th>
                            <th>Vehicle</th>
                            <th>Type</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {otherAvailableRides.slice(0, 10).map((ride) => (
                            <tr key={ride.RIDE_ID}>
                              <td>{ride.RIDE_ID}</td>
                              <td>{sanitizeText(ride.FROM_LOCATION)}</td>
                              <td>{sanitizeText(ride.TO_LOCATION)}</td>
                              <td>{sanitizeText(ride.VEHICLE_TYPE)}</td>
                              <td>
                                <span
                                  className={`ride-type-badge ${
                                    ride.IS_SPECIAL_TASK ? "special" : "regular"
                                  }`}
                                >
                                  {ride.IS_SPECIAL_TASK
                                    ? "Special Task Ride"
                                    : "Regular Ride"}
                                </span>
                              </td>
                              <td>
                                <button
                                  className="accept-btn"
                                  onClick={() => handleAcceptRide(ride.RIDE_ID)}
                                  disabled={loading}
                                >
                                  {loading ? "Processing..." : "Accept Ride"}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="no-rides-message">
                      No additional rides available
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="ride-history-section">
              <h3 className="section-title">Completed Rides</h3>
              {rideHistory.length > 0 ? (
                <div className="table-container">
                  <table className="rides-table">
                    <thead>
                      <tr>
                        <th>Ride ID</th>
                        <th>From</th>
                        <th>To</th>
                        <th>Vehicle Type</th>
                        <th>Ride Type</th>
                        <th>Created At</th>
                        <th className="completed-at-heading">Completed At</th>
                      </tr>
                    </thead>

                    <tbody>
                      {rideHistory.map((ride) => (
                        <tr key={ride.ride_id || ride.RIDE_ID}>
                          <td>{ride.ride_id || ride.RIDE_ID}</td>
                          <td>{ride.from_location || ride.FROM_LOCATION}</td>
                          <td>{ride.to_location || ride.TO_LOCATION}</td>
                          <td>
                            {ride.vehicle_type || ride.VEHICLE_TYPE || "N/A"}
                          </td>
                          <td>
                            <span
                              className={`ride-type-badge ${
                                (ride.ride_type || ride.RIDE_TYPE) ===
                                "Special Task"
                                  ? "special"
                                  : "regular"
                              }`}
                            >
                              {ride.ride_type || ride.RIDE_TYPE}
                            </span>
                          </td>
                          <td>
                            {formatDate(ride.created_at || ride.CREATED_AT)}
                          </td>
                          <td>
                            {formatDate(ride.completed_at || ride.COMPLETED_AT)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="no-rides-message">
                  No ride history available
                </div>
              )}
            </div>
          )}

          {showRequesterPopup && (
            <div className="confirmation-overlay">
              <div
                className="confirmation-box"
                style={{ position: "relative" }}
              >
                {/* ❌ X close button */}
                <button
                  onClick={() => setShowRequesterPopup(false)}
                  style={{
                    position: "absolute",
                    top: "10px",
                    right: "10px",
                    border: "none",
                    background: "transparent",
                    fontSize: "18px",
                    color: "black",
                    cursor: "pointer",
                  }}
                >
                  ✕
                </button>

                <h3>Requester Details</h3>
                <p>
                  <strong>Name:</strong> {requesterDetails?.name || "N/A"}
                </p>
                <p>
                  <strong>Mobile:</strong> {requesterDetails?.mobile || "N/A"}{" "}
                  <a
                    href="tel:9876543210"
                    style={{
                      marginLeft: "10px",
                      textDecoration: "none",
                      fontWeight: "bold",
                      color: "#10b981",
                    }}
                  >
                    📞
                  </a>
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default DriverDashboard;
